import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TextInput, { Validation } from '../../../general-ui/text-input/text';
import Modal from '../../../general-ui/modal/modal';
import RadioGroup from '../../../general-ui/radio-group/radio-group';
import Datepicker from '../../../general-ui/datepicker/datepicker';
import "react-datepicker/dist/react-datepicker.css";
import TagSelectInput from '../../../general-ui/tag-select/tag-select';
import LanguageSelectInput from '../../../general-ui/select/language-select';
import { Session, SessionTypesEnum, SessionTypesFeatures } from "../../../../types/working-model";
import TranslateLangs from '../../../../utils/translate-langs.json';
import { GetChannelTracks } from "../../../../connection/create-event";
import Timepicker from "../../../general-ui/time-picker/timepicker";
import { useSelector } from "react-redux";
import { EventsState } from "../../../../store/types";
import { updateTranslateKey } from "../../../../utils/utils";
import { PrimaryTooltip } from "./session-modal";
import useRequiredFields from "../../../../utils/use-required-fields";
import classNames from "classnames";
import ReactDOM from "react-dom";
import { useParams } from "react-router";
import { ParamsProps } from "../../../live-event/live-event";
import ModalComponent from "../../../general-ui/modal/modal";

interface EditSessionModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSave: (session: Session) => void;
	startingSession: Session
}

const EditSessionModal: React.FC<EditSessionModalProps> = ({ isOpen, onClose, onSave, startingSession }) => {
	const {
		AuthReducer: {
			user,
			token
		},
		CreateEventReducer: {
			workingEvent,
			newEventRequest
		}
	} = useSelector((state: EventsState) => state);

	const workingSession = useRef<Session>(startingSession);
	const [selectedSessionType, setSelectedSessionType] = useState(startingSession.session_type);
	const [defaultTracks, setDefaultTracks] = useState([]);
	const startingDuration = startingSession.timestamp ? new Date(startingSession.end_timestamp! - startingSession.timestamp!).valueOf() / 60000 : 60; //dont use getMinutes() cause it won't account for hours
	const [duration, setDuration] = useState(startingDuration);
	const [durationIsSet, setDurationIsSet] = useState(startingSession.session_type !== SessionTypesEnum.onDemand);
	const [sessionName, setSessionName] = useState(startingSession.title);
	const [date, setDate] = useState<Date | undefined>(startingSession.timestamp ? new Date(startingSession.timestamp as number) : undefined);
	const [errorMessage, setErrorMessage] = useState('');
	const [pastEventOpen, setPastEventOpen] = useState(false);

	const { language } = useParams() as ParamsProps;

	const {
		requiredFields,
		updateRequiredField,
		resetRequiredFields,
		hasCompletedRequiredFields
	} = useRequiredFields(['name', 'duplicateName'], {
		name: (string: string) => !!string.trim(),
		// if the name is duplicate then return false to trigger validation
		duplicateName: (isDuplicate: boolean) => {
			if (isDuplicate) setErrorMessage('Session name already used for this Event');
			if (!isDuplicate) setErrorMessage('');
			return !isDuplicate;
		}
	});

	const sessionNames: { name: string, id: string | undefined }[] = useMemo(() => {
		const existingNames = workingEvent ? workingEvent.sessions.map(session => ({ name: session.title.base, id: session.session.toString() })) : [];
		const requestNames = newEventRequest ? newEventRequest.sessions.map(session => ({ name: session.title, id: session?.uid })) : [];
		return [...existingNames, ...requestNames];
	}, [workingEvent, newEventRequest]);


	const checkName = useCallback((sessionName: string) => {
		// if same name as a different session, then duplicate
		const isDuplicate = sessionNames.some(name => name.name === sessionName && workingSession.current.session.toString() !== name.id);
		updateRequiredField('duplicateName', isDuplicate);
	}, [sessionNames, updateRequiredField]);

	const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		checkName(((e.target as HTMLInputElement).value));
	}, [checkName]);

	const finished = useCallback(() => {
		// check if new date for broadcast is in past
		// cancel will keep original date, event if in past
		if (workingSession.current) {
			if (selectedSessionType === SessionTypesEnum.broadcast && date) {
				if (date.valueOf() < Date.now()) {
					setPastEventOpen(true);
					return;
				}
			}
		}
		if (hasCompletedRequiredFields({ name: sessionName.base })) onSave(workingSession.current);
	}, [hasCompletedRequiredFields, onSave, sessionName.base, date, selectedSessionType]);

	const handleSetDate = useCallback((date: Date) => {
		if (workingSession.current) {
			setDate(date);
			workingSession.current.timestamp = date.valueOf();
			workingSession.current.end_timestamp = date.valueOf() + (1000 * 60 * duration);
		}
	}, [duration]);

	const handleSetDuration = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (workingSession.current) {
			setDuration(e.target.valueAsNumber);

			setDurationIsSet(!isNaN(e.target.valueAsNumber));

			if (!isNaN(e.target.valueAsNumber)) {
				if (workingSession.current.timestamp) { workingSession.current.end_timestamp = workingSession.current.timestamp + (1000 * 60 * e.target.valueAsNumber); }
			}
		}
	}, []);

	const handleRadio = useCallback((value: any) => {
		setSelectedSessionType(value);
		if (workingSession.current) {
			workingSession.current.session_type = value;
			if (!SessionTypesFeatures[workingSession.current.session_type].has_start_time) {
				setDate(undefined);
			}
		}
	}, []);

	const setTracks = useCallback((tracks: string[]) => {
		if (workingSession.current) { workingSession.current.tracks = tracks; }
	}, []);

	const setLanguages = useCallback((langs: string[], defaultLang: string) => {
		if (workingSession.current) {
			//user picked no langauges, force english
			if (!langs[0])
				langs = ['en'];

			workingSession.current.languages = langs;

			let defLang = defaultLang;

			//no default language was picked
			if (!defaultLang) {

				//pick the first selected language
				defLang = langs[0];
			}

			workingSession.current.default_language = defLang;

			//tells the API to translate modules
			workingSession.current.translate_all = true;
			// workingSession.current.modules.map(module => {

			// })
		}
	}, []);

	const handleSessionName = useCallback(({ target }: React.ChangeEvent<HTMLInputElement>) => {
		if (workingSession.current) {
			updateRequiredField('name', target.value);
			const newTranslateKey = updateTranslateKey({
				translateString: workingSession.current.title,
				input: target.value,
				language,
				baseLanguage: workingSession.current.default_language
			});
			workingSession.current.title = newTranslateKey;
			setSessionName(newTranslateKey);
		}
	}, [updateRequiredField, language]);

	function formValid() {
		if (!SessionTypesFeatures[workingSession.current.session_type].has_start_time) {
			return !!sessionName;
		} else {
			return !!sessionName && !!date && durationIsSet && !isNaN(duration);
		}
	}

	const handleCancel = useCallback(() => {
		onClose();
		setTimeout(() => resetRequiredFields(), 300);
	}, [onClose, resetRequiredFields]);

	const loadTracks = useCallback(async function() {
		try {
			const tracks = await GetChannelTracks(user!.active_channel, token!);

			setDefaultTracks(tracks);
		} catch (e) {
			console.error(e);
		}
	}, [token, user]);

	useEffect(() => {
		if (isOpen) {
			loadTracks();
		} else {
			//reset the whole thing on close
			workingSession.current = startingSession;
			ReactDOM.unstable_batchedUpdates(() => {
				setSelectedSessionType(startingSession.session_type);
				setDuration(startingDuration);
				setDurationIsSet(!!SessionTypesFeatures[startingSession.session_type].has_start_time);
				setSessionName(startingSession.title);
				setDate(startingSession.timestamp ? new Date(startingSession.timestamp!) : undefined);
			});
		}

	}, [isOpen, loadTracks, startingDuration, startingSession]);


	const closePastEvent = () => {
		setPastEventOpen(false);
	};

	const validName = (requiredFields.name === Validation.normal && requiredFields.duplicateName === Validation.normal) ? Validation.normal : Validation.error;

	return (
		<>
			<Modal
				open={isOpen}
				onRequestClose={onClose}
				title={"Edit Session"}
				className="edit-session-modal"
				cancellable
				closeable={false}
				footer={(
					<div className="edit-session-modal-footer">
						<span className={classNames("error-message", { toast: errorMessage })}>{errorMessage}</span>
						<div className="action-buttons">
							<button onClick={handleCancel} style={{ marginRight: 16 }}>Cancel</button>
							<button disabled={!formValid()} onClick={finished} className="primary">Save</button>
						</div>
					</div>
				)}
			>
				<TextInput
					label={"Session Title *"}
					tooltip={[PrimaryTooltip, 'This is the first impression you make. Try to make it useful and convey the idea that the main benefit is somehow unique.']}
					onChange={handleSessionName}
					placeholder={"Enter session title"}
					value={sessionName.base}
					onBlur={(e) => updateRequiredField('name', e.target.value)}
					valid={validName}
					onKeyUp={handleKeyUp}
				/>

				<TagSelectInput
					label={"Tracks"}
					tooltip={[PrimaryTooltip, 'Tracks help organize and distribute content in a meaningful way.']}
					placeholder={"Select or enter tracks"}
					onChange={setTracks}
					defaultTags={defaultTracks}
					initialTags={startingSession.tracks || undefined}
					dropdownContainerStyles={{
						maxWidth: 'calc(100% - 80px)',
						margin: '0 auto',
					}}
					noWhitespace
					maxTags={20}
					onError={(s) => {
						if (s === 'MAX TAGS') setErrorMessage('Twenty is the max number of tracks');
						setTimeout(() => setErrorMessage(''), 2500);
					}}
				/>

				<LanguageSelectInput
					label={"Languages"}
					tooltip={[PrimaryTooltip, 'This is the tooltip for the Language selection']}
					placeholder={"Select languages"}
					onChange={setLanguages}
					languages={TranslateLangs as any}
					initialValues={startingSession.languages}
					initialDefaultLanguage={startingSession.default_language}
				/>

				<RadioGroup
					options={
						// FUTURE DEV - remove filter for future implementation of breakout rooms
						// Object.values(SessionTypesFeatures).map(value => ({
						Object.values(SessionTypesFeatures).filter(value => value.name !== "breakout").map(value => ({
							value: value.name,
							label: value.display_name
						}))
					}
					onChange={handleRadio}
					selected={selectedSessionType}
				/>

				{SessionTypesFeatures[selectedSessionType].has_start_time && (
					<div className="field-group broadcast-group">
						<div className="broadcast-date">
							<Datepicker
								onChange={handleSetDate}
								label={"Date"}
								defaultValue={date}
								portalId="date-portal"
							/>
						</div>
						<div className="broadcast-time">
							<Timepicker
								onChange={handleSetDuration}
								label={"Duration (minutes)"}
								placeholder={"Enter a duration in minutes"}
								initialValue={SessionTypesFeatures[startingSession.session_type].has_start_time ? duration : undefined}
							/>
						</div>

					</div>
				)}
			</Modal>
			<ModalComponent
				open={pastEventOpen}
				onRequestClose={closePastEvent}
				cancellable={false}
				closeable={false}
				title={"Events in the past"}
				footer={
					<div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
						<button className={'primary'} onClick={closePastEvent}>Close</button>
					</div>
				}
			>
				<div className="past-events-modal" style={{ marginTop: 8 }}>
					<p>Events must be scheduled in the present or future.</p>
				</div>
			</ModalComponent>
		</>
	);
};

export default EditSessionModal;